import { useModel } from '@umijs/max';
import { message } from 'antd';
import { useCallback, useState } from 'react';
import type { MerchantInfo } from '@/models/merchant';
import { getUserMerchants } from '@/services/business/user';

export interface ActingAsMerchantOwner {
  userId: number;
  username: string;
  displayName?: string;
  email: string;
}

export const ACTING_AS_STORAGE_KEY = 'aimerpay_acting_as_merchant_owner';
const STORAGE_KEY = ACTING_AS_STORAGE_KEY;
const MERCHANTS_CACHE_KEY = 'aimerpay_acting_as_merchants_cache';

function readFromStorage(): ActingAsMerchantOwner | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeToStorage(owner: ActingAsMerchantOwner | null) {
  if (owner) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(owner));
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(MERCHANTS_CACHE_KEY);
  }
}

function readMerchantsCache(): MerchantInfo[] {
  try {
    const raw = sessionStorage.getItem(MERCHANTS_CACHE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeMerchantsCache(merchants: MerchantInfo[]) {
  sessionStorage.setItem(MERCHANTS_CACHE_KEY, JSON.stringify(merchants));
}

export default function useActingAsMerchantModel(): {
  actingAsMerchantOwner: ActingAsMerchantOwner | null;
  enterMerchantOwnerMode: (owner: ActingAsMerchantOwner) => Promise<void>;
  exitMerchantOwnerMode: () => void;
  getMerchantsCache: () => MerchantInfo[];
} {
  const [actingAsMerchantOwner, setActingAsMerchantOwner] =
    useState<ActingAsMerchantOwner | null>(readFromStorage);
  const merchantModel = useModel('merchant');
  const { initialState } = useModel('@@initialState');

  const enterMerchantOwnerMode = useCallback(
    async (owner: ActingAsMerchantOwner): Promise<void> => {
      writeToStorage(owner);
      setActingAsMerchantOwner(owner);

      try {
        // Fetch this MerchantOwner's partners
        const response = await getUserMerchants(owner.userId);
        const partners = response?.data || [];

        if (partners.length > 0) {
          const merchantList: MerchantInfo[] = partners.map(
            (p: BusinessAPI.Partner) => ({
              id: p.id,
              partnerName: p.partnerName,
              legalName: p.legalName,
              type: p.type,
              status: p.status,
              email: p.email,
              phone: p.phone,
              hasAdyenAccountHolder: p.hasAdyenAccountHolder,
              balanceAccountCount: p.balanceAccountCount,
            }),
          );

          // Cache in sessionStorage for page refresh
          writeMerchantsCache(merchantList);

          // Pass SuperAdmin's own userId so localStorage keys match access.ts checks
          const currentUserId = String(
            initialState?.currentUser?.id ||
              initialState?.currentUser?.userid ||
              '',
          );
          merchantModel.loadMerchants(
            merchantList,
            currentUserId,
            merchantList[0].id,
          );
        }
      } catch (_error) {
        // Roll back on failure
        writeToStorage(null);
        setActingAsMerchantOwner(null);
        message.error('Failed to load merchant data');
        return;
      }

      // Use full page reload to ensure access flags are recalculated
      // from scratch. history.push + flushSync/setInitialState can fail
      // because UmiJS access plugin may cache the result and not
      // re-evaluate before the route renders → 403.
      window.location.href = '/dashboard';
    },
    [merchantModel, initialState?.currentUser],
  );

  const exitMerchantOwnerMode = useCallback((): void => {
    writeToStorage(null);
    setActingAsMerchantOwner(null);
    merchantModel.clearMerchant();
    // Clear impersonation sessionStorage
    sessionStorage.removeItem('aimerpay_acting_as_merchant_owner');
    sessionStorage.removeItem('aimerpay_acting_as_merchants_cache');
    window.location.href = '/admin/merchant-owners';
  }, [merchantModel]);

  return {
    actingAsMerchantOwner,
    enterMerchantOwnerMode,
    exitMerchantOwnerMode,
    getMerchantsCache: readMerchantsCache,
  };
}

export function getActingAsMerchantOwner(): ActingAsMerchantOwner | null {
  return readFromStorage();
}

export function getActingAsMerchantsCache(): MerchantInfo[] {
  return readMerchantsCache();
}

// Legacy alias for backward compatibility
export const getActingAsMerchant = getActingAsMerchantOwner;
